import { Link } from "react-router-dom";
import { BookMarked, Shield, ArrowLeft } from "lucide-react";

// Last updated date — bump this when you make material changes.
const LAST_UPDATED = "13 February 2026";

// Update this only after consulting your own legal advisor.
const CONTROLLER = {
  name: "Studict",
  email: "contact@studictsmartdictionary.com",
  country: "United Kingdom",
};

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-yellow-50">
      <header className="px-6 sm:px-8 lg:px-12 py-6 flex items-center justify-between border-b-2 border-slate-900 bg-white">
        <Link to="/" data-testid="privacy-back" className="font-display text-xl sm:text-2xl font-black flex items-center gap-2">
          <BookMarked className="w-6 h-6" strokeWidth={2.5}/> Studict
        </Link>
        <Link to="/" className="text-xs sm:text-sm font-bold inline-flex items-center gap-1 underline hover:no-underline">
          <ArrowLeft className="w-3 h-3" strokeWidth={2.5}/> Home
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="privacy-page">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-7 h-7" strokeWidth={2.5}/>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Privacy Policy</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight">Your data, explained plainly</h1>
        <p className="text-sm text-slate-500 mt-2">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-slate max-w-none mt-8 prose-headings:font-black prose-headings:font-display prose-h2:text-2xl prose-h2:mt-10 prose-h3:text-lg prose-p:leading-relaxed">

          <h2>1. Who we are</h2>
          <p>
            Studict ("we", "us") is a study companion app run from {CONTROLLER.country}. We are the
            data controller for the personal information described in this policy. Contact:{" "}
            <a href={`mailto:${CONTROLLER.email}`}>{CONTROLLER.email}</a>.
          </p>
          <p>
            This policy is written in line with the UK General Data Protection Regulation
            (UK GDPR), the Data Protection Act 2018 and, where applicable, the EU GDPR. If
            you are in the United States we honour requests broadly equivalent to the
            California Consumer Privacy Act (CCPA/CPRA).
          </p>

          <h2>2. What we collect</h2>
          <h3>Account &amp; identity</h3>
          <ul>
            <li><strong>Email address</strong> (required for sign-in via Google, magic-link or passkey)</li>
            <li><strong>Display name &amp; profile picture</strong> (from Google sign-in only; you can edit/remove these)</li>
            <li><strong>Unique user ID</strong> we generate for you (not your government ID)</li>
            <li><strong>Plan, payment timestamps, billing history</strong> (free / Pro / Pro Plus + Stripe references)</li>
          </ul>

          <h3>Content you create</h3>
          <ul>
            <li>Glossary entries, categories, study materials (text, PDFs, audio, video, images you upload)</li>
            <li>Quiz attempts, test dates you add, draft assignments you generate</li>
            <li>Photos you submit for AI OCR are processed and the extracted text is saved; the original image is deleted within 24 hours unless you choose to save it</li>
          </ul>

          <h3>Technical &amp; usage data</h3>
          <ul>
            <li>Session cookies and Bearer tokens (so you stay signed in)</li>
            <li>Server logs (IP address, browser type, paths visited) kept for at most 30 days for security and abuse prevention</li>
            <li>Push-notification subscription endpoint (only if you opt in)</li>
            <li>Aggregate counts (e.g. how many users on each plan) — never linked to individuals in admin views</li>
          </ul>

          <h3>Payments</h3>
          <p>
            All card data is handled by <strong>Stripe</strong> directly. We never see or store
            your card number, expiry, CVC or banking credentials — we only receive a Stripe
            customer reference and the amount you paid.
          </p>

          <h3>Biometric data — what we do <em>not</em> collect</h3>
          <p>
            When you enable a passkey (Face ID, Touch ID, Windows Hello, Android fingerprint
            or device PIN), <strong>no fingerprint, face scan, or any biometric template ever
            leaves your device or is sent to Studict</strong>. Your device performs the
            biometric check locally and only sends Studict a cryptographic signature proving
            it succeeded. We store a public key associated with your account — not anything
            that could identify you biometrically.
          </p>

          <h2>3. Lawful basis (UK/EU GDPR Art. 6)</h2>
          <ul>
            <li><strong>Contract (Art. 6(1)(b))</strong> — to provide the Studict service you signed up for: storing your glossary, processing payments, sending account emails.</li>
            <li><strong>Legitimate interests (Art. 6(1)(f))</strong> — keeping the service secure, preventing abuse, basic product analytics in aggregate.</li>
            <li><strong>Consent (Art. 6(1)(a))</strong> — push notifications, marketing/promotional emails (you can withdraw anytime in Settings).</li>
            <li><strong>Legal obligation (Art. 6(1)(c))</strong> — keeping tax/payment records where required by HMRC or equivalent.</li>
          </ul>
          <p>
            We do not knowingly process the personal data of children under 13. Studict is
            intended for use by secondary, college and university students. If you believe a
            child under 13 has provided data, contact us and we will delete it.
          </p>

          <h2>4. How long we keep your data</h2>
          <ul>
            <li><strong>Account &amp; content</strong>: for as long as your account is active, then 30 days after deletion request before permanent removal (so you can change your mind).</li>
            <li><strong>Payment records</strong>: 7 years (tax/legal obligation).</li>
            <li><strong>Server logs</strong>: 30 days.</li>
            <li><strong>Magic-link tokens</strong>: 15 minutes.</li>
            <li><strong>Push-notification subscription</strong>: until you unsubscribe.</li>
            <li><strong>Photos submitted for OCR</strong>: deleted within 24 hours unless you save them to Materials.</li>
          </ul>

          <h2>5. Who we share data with</h2>
          <p>We only share what is necessary for the service to run:</p>
          <ul>
            <li><strong>Stripe</strong> — payment processing (their <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">privacy policy</a>)</li>
            <li><strong>Google</strong> — only if you sign in with Google (email, name, profile picture)</li>
            <li><strong>Resend</strong> — outbound transactional emails (sign-in links, receipts, reminders)</li>
            <li><strong>MongoDB Atlas / Emergent infrastructure</strong> — encrypted database hosting</li>
            <li><strong>Google Gemini / OpenAI</strong> — only when you actively use AI features (OCR scan, quiz generation, draft assistant). Inputs are sent to the relevant API for processing and are not used to train their models.</li>
            <li><strong>PostHog</strong> — privacy-friendly product analytics (page views, feature usage, anonymous session replays of <em>your own</em> Studict sessions). We use this to fix bugs and understand which features help students. PostHog is GDPR-compliant; sensitive inputs (passwords, payment fields) are masked. You can opt out by enabling "Do Not Track" or a browser privacy extension. <a href="https://posthog.com/privacy" target="_blank" rel="noreferrer">PostHog privacy policy</a>.</li>
          </ul>
          <p>We never sell your data. We never use it to train any AI model. We never use third-party advertising or marketing trackers.</p>

          <h2>6. International transfers</h2>
          <p>
            Some of our processors operate servers outside the UK/EU. Where this is the
            case, we rely on the UK International Data Transfer Agreement (IDTA), Standard
            Contractual Clauses, or each provider's adequacy mechanism to keep your data
            protected to the same standard.
          </p>

          <h2>7. Your rights</h2>
          <p>Under UK/EU GDPR (and substantially under CCPA/CPRA) you have the right to:</p>
          <ul>
            <li><strong>Access</strong> a copy of the personal data we hold about you</li>
            <li><strong>Rectify</strong> data that is inaccurate or incomplete</li>
            <li><strong>Erase</strong> your account and personal data ("right to be forgotten")</li>
            <li><strong>Restrict or object</strong> to certain processing</li>
            <li><strong>Port</strong> your data to another service in a machine-readable format</li>
            <li><strong>Withdraw consent</strong> for marketing emails / push notifications at any time</li>
            <li><strong>Lodge a complaint</strong> with the UK Information Commissioner's Office (ICO) — <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer">ico.org.uk</a> — or your local data protection authority</li>
          </ul>

          <h2>8. How to make a request</h2>
          <p>
            Email <a href={`mailto:${CONTROLLER.email}`}>{CONTROLLER.email}</a> with subject{" "}
            <strong>"Data request — [Access / Erase / Port / Rectify]"</strong> and the email
            address on your Studict account. We respond within <strong>30 days</strong>
            (extendable to 90 days for unusually complex requests, with notice). We do this
            free of charge unless the request is manifestly excessive.
          </p>
          <p>
            For a quicker self-service path: sign in → <strong>Settings → Delete my account</strong>{" "}
            removes your account and content immediately, with a 30-day grace period.
          </p>

          <h2>9. Cookies</h2>
          <p>
            We use a small number of essential cookies that don't require consent under
            PECR/UK GDPR:
          </p>
          <ul>
            <li><code>session_token</code> — keeps you signed in (HTTP-only, secure, SameSite=Lax)</li>
          </ul>
          <p>
            We also store a Bearer token in your browser's <code>localStorage</code> as a
            fallback for browsers that block cookies. We do <strong>not</strong> use any
            advertising cookies or ad pixels that track you across other websites. We do use
            <strong>PostHog</strong> for first-party product analytics (see Section 5).
          </p>

          <h2>10. Security</h2>
          <p>
            We use HTTPS (TLS) everywhere, encrypted database storage, and bcrypt-equivalent
            secure hashing for any secrets. Despite reasonable safeguards, no internet service
            is 100% secure — if a breach affects your data, we will notify you within{" "}
            <strong>72 hours</strong> as required by UK GDPR.
          </p>

          <h2>11. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. When material changes happen we will
            email all active users and post a notice on the homepage. The "Last updated" date
            at the top always reflects the current version.
          </p>

          <h2>12. Contact us</h2>
          <p>
            Email <a href={`mailto:${CONTROLLER.email}`}>{CONTROLLER.email}</a>. We aim to
            reply within 5 working days for general queries and within 30 days for formal
            data requests.
          </p>
        </div>

        <div className="mt-12 pt-6 border-t-2 border-slate-300 flex flex-wrap gap-4 text-sm font-bold">
          <Link to="/" className="underline">Home</Link>
          <Link to="/terms" className="underline">Terms of Service</Link>
          <Link to="/support" className="underline">Contact support</Link>
        </div>
      </main>
    </div>
  );
}
